import { useMutation, useQuery } from "@tanstack/react-query";
import { useSecretManager } from "Hooks/useSecretManager";
import { JobDocument } from "Models/job.document";
import CustomerPortalService from "Services/customer.portal.service";
import env from "Util/env";
import { generateTokenBySecret } from "Util/generateTokenBySecret";
import { DocumentKeys } from "Util/queries";

import JobDocumentService from "../services/job.document.service";

interface UpdateSharedParams {
  id: number;
  isShared: boolean;
}

interface UseUpdateDocumentIsSharedOptions {
  onUpdateSuccess: (data: JobDocument, param: UpdateSharedParams) => void;
  onUpdateError: (error: unknown, param: UpdateSharedParams, context: unknown) => void;
  onRequest?: (param: UpdateSharedParams) => void;
}

interface UseUploadDocumentOptions {
  onUploadSuccess: (data: number, jobDocument: JobDocument) => void;
  onUploadError: (error: unknown, jobDocument: JobDocument, context: unknown) => void;
  onRequest?: (jobDocument: JobDocument) => void;
}

interface UpdateReviewParams {
  id: number;
  reviewStatus: JobDocument["reviewStatus"];
}

interface UseUpdateDocumentReviewOptions {
  onUpdateSuccess: (data: JobDocument, param: UpdateReviewParams) => void;
  onUpdateError: (error: unknown, param: UpdateReviewParams, context: unknown) => void;
  onRequest?: (param: UpdateReviewParams) => void;
}

interface UpdateIsRequestedParams {
  id: number;
  isRequested: boolean;
}
interface UseUpdateDocumentIsRequestedOptions {
  onUpdateSuccess: (data: JobDocument, param: UpdateIsRequestedParams) => void;
  onUpdateError: (error: unknown, param: UpdateIsRequestedParams, context: unknown) => void;
  onRequest?: (param: UpdateIsRequestedParams) => void;
}

interface JobDocumentCreatePayload {
  jobId: number;
  name: string;
  dueDate: string;
}

interface UseCreateJobDocumentOptions {
  onCreateSuccess: (data: JobDocument, jobDocument: JobDocumentCreatePayload) => void;
  onCreateError?: (error: unknown, jobDocument: JobDocumentCreatePayload, context: unknown) => void;
  onRequest?: (jobDocument: JobDocumentCreatePayload) => void;
}

export const useActionJobDocument = () => {
  const { secret } = useSecretManager(env.SECRET_ID);

  const getGetCustomerPortalJobDocumentsByJobId = (jobId?: number) => {
    return useQuery(
      ["job.document.getGetCustomerPortalJobDocumentsByJobId", jobId],
      () => {
        const token = generateTokenBySecret(jobId, secret)!;

        if (!token) return;

        return CustomerPortalService.getJobDocuments(token).then(({ data }) => data);
      },
      {
        enabled: !!(jobId && secret),
        cacheTime: 0,
        refetchOnWindowFocus: false,
      }
    );
  };

  const getGetJobDocumentsByJobId = (jobId?: number) => {
    return useQuery(
      ["job.document.getGetJobDocumentsByJobId", jobId],
      async () => {
        if (!jobId) return;

        return JobDocumentService.getListJobId(jobId).then(({ data }) => data);
      },
      {
        enabled: !!jobId,
        cacheTime: 0,
        refetchOnWindowFocus: false,
      }
    );
  };

  const useUpdateIsRequestDocument = (options: UseUpdateDocumentIsRequestedOptions) =>
    useMutation({
      mutationKey: DocumentKeys.update(),
      mutationFn: ({ id, isRequested }: UpdateIsRequestedParams) =>
        JobDocumentService.updateIsRequested(id, { isRequested }),
      onSuccess: ({ data }, variables) => {
        if (data) {
          options.onUpdateSuccess(data, variables);
        }
      },
      onError(error, variables, context) {
        options.onUpdateError(error, variables, context);
      },
      onMutate(variables) {
        options.onRequest?.(variables);
      },
    });

  const useUpdateReviewDocument = (options: UseUpdateDocumentReviewOptions) =>
    useMutation({
      mutationKey: DocumentKeys.update(),
      mutationFn: ({ id, reviewStatus }: UpdateReviewParams) =>
        JobDocumentService.updateReview(id, { reviewStatus }),
      onSuccess: ({ data }, variables) => {
        if (data) {
          options.onUpdateSuccess(data, variables);
        }
      },
      onError(error, variables, context) {
        options.onUpdateError(error, variables, context);
      },
      onMutate(variables) {
        options.onRequest?.(variables);
      },
    });

  const useUpdateShareDocument = (options: UseUpdateDocumentIsSharedOptions) =>
    useMutation({
      mutationKey: DocumentKeys.update(),
      mutationFn: ({ id, isShared }: UpdateSharedParams) =>
        JobDocumentService.updateIsShared(id, { isShared }),
      onSuccess: ({ data }, variables) => {
        if (data) {
          options.onUpdateSuccess(data, variables);
        }
      },
      onError(error, variables, context) {
        options.onUpdateError(error, variables, context);
      },
      onMutate(variables) {
        options.onRequest?.(variables);
      },
    });

  const useUploadDocument = (options: UseUploadDocumentOptions) =>
    useMutation({
      mutationKey: DocumentKeys.upload(),
      mutationFn: (jobDocument: JobDocument) => JobDocumentService.uploadFile(jobDocument),
      onSuccess: ({ data }, variables) => {
        if (data) {
          options.onUploadSuccess(data, variables);
        }
      },
      onError(error, variables, context) {
        options.onUploadError(error, variables, context);
      },
      onMutate(variables) {
        options.onRequest?.(variables);
      },
    });

  const useCreateDocument = (options: UseCreateJobDocumentOptions) =>
    useMutation({
      mutationKey: DocumentKeys.create(),
      mutationFn: (payload: JobDocumentCreatePayload) => JobDocumentService.create(payload),
      onSuccess: ({ data }, variables) => {
        if (data) {
          options.onCreateSuccess(data, variables);
        }
      },
      onError(error, variables, context) {
        options.onCreateError?.(error, variables, context);
      },
      onMutate(variables) {
        options.onRequest?.(variables);
      },
    });

  const useClearDocument = (onUploadSuccess: (jobDocument: JobDocument) => void) =>
    useMutation({
      mutationKey: DocumentKeys.clear(),
      mutationFn: (jobDocument: JobDocument) =>
        JobDocumentService.clear(jobDocument.id, jobDocument),
      onSuccess: (response) => {
        if (response?.data) {
          onUploadSuccess(response.data);
        }
      },
    });

  return {
    useCreateDocument,
    useUploadDocument,
    useUpdateIsRequestDocument,
    useUpdateShareDocument,
    useUpdateReviewDocument,
    useClearDocument,
    getGetCustomerPortalJobDocumentsByJobId,
    getGetJobDocumentsByJobId,
  };
};
